import React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { ErrorMessage, useField } from "formik";
import { PasswordInputComponentProps } from "./password-input-component.props";
import "./password-input-component.scss";

/**
 * PasswordInputComponent renders a password field with show/hide toggle
 * and Formik integration via useField.
 */
const PasswordInputComponent: React.FC<PasswordInputComponentProps> = ({
  fieldName = "",
  label,
  placeholder = "",
  optional = false,
  disabled,
  value,
  onChange,
  minlength,
  maxlength,
  className = "",
  sx = {},
}) => {
  const [field] = useField(fieldName);
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <Box>
      <div className="form-password-input-component">
        {label && (
          <InputLabel required={optional} htmlFor={fieldName}>
            {label}
          </InputLabel>
        )}
        <FormControl>
          <OutlinedInput
            {...field}
            name={fieldName}
            placeholder={placeholder}
            required={optional}
            disabled={disabled}
            id={fieldName}
            value={value}
            onChange={onChange}
            slotProps={{
              input: {
                id: fieldName,
                name: fieldName,
                required: optional,
                minLength: minlength,
                maxLength: maxlength,
              },
            }}
            type={showPassword ? "text" : "password"}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label={
                    showPassword ? "display the password" : "hide the password"
                  }
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            }
            className={className}
            sx={sx}
          />
        </FormControl>
        <ErrorMessage
          name={fieldName}
          component="div"
          className="error-message"
        />
      </div>
    </Box>
  );
};

export default PasswordInputComponent;
